import type { EnhancementService } from "@/types/enhance";

/**
 * Geocoding enhancement service configuration
 * This is a pure configuration - no service-specific logic needed
 */
export const geocodingService: EnhancementService = {
  id: "geocoding",
  name: "Geocoding",
  description: "Convert addresses to coordinates",
  endpoint: "/geocoding/v1/search/bulk",
  method: "POST",
  supportsBatch: true,
  maxBatchSize: 1000,
  batchRequestFormat: "array",
  responseFormat: "direct",
  defaultQueryParams: {
    size: 1,
  },
  authentication: {
    type: "domain",
    envVar: "VITE_STADIAMAPS_API_KEY",
  },
  baseUrl: "https://api-eu.stadiamaps.com",
  getEndpoint: () => "/v1/search",
  requestFields: [
    {
      id: "address",
      label: "Address",
      description: "Address columns to geocode",
      type: "string",
      required: false,
      apiFieldName: "text",
    },
  ],
  defaultResponseFields: ["latitude", "longitude"],
  responseFields: [
    {
      id: "latitude",
      label: "Latitude",
      description: "Latitude coordinate",
      type: "number",
      apiResponsePath: "features[0].geometry.coordinates[1]",
      example: "40.7128",
    },
    {
      id: "longitude",
      label: "Longitude",
      description: "Longitude coordinate",
      type: "number",
      apiResponsePath: "features[0].geometry.coordinates[0]",
      example: "-74.0060",
    },
    {
      id: "label",
      label: "Matched Address",
      description: "Formatted address label from geocoding service",
      type: "string",
      apiResponsePath: "features[0].properties.label",
      example: "123 Main St, New York, NY 10001",
    },
    {
      id: "confidence",
      label: "Confidence",
      description: "Match confidence score (1-10, where 10 is highest)",
      type: "number",
      apiResponsePath: "features[0].properties.confidence",
      example: "10",
    },
    {
      id: "accuracy",
      label: "Accuracy",
      description: "Accuracy level: point, interpolated, street, etc.",
      type: "string",
      apiResponsePath: "features[0].properties.accuracy",
      example: "point",
    },
    {
      id: "match_type",
      label: "Match Type",
      description: "Type of match: exact, fallback, etc.",
      type: "string",
      apiResponsePath: "features[0].properties.match_type",
      example: "exact",
    },
    {
      id: "name",
      label: "Name",
      description: "Name of the place/address",
      type: "string",
      apiResponsePath: "features[0].properties.name",
      example: "27 Põhja pst",
    },
    {
      id: "street",
      label: "Street",
      description: "Street name",
      type: "string",
      apiResponsePath: "features[0].properties.street",
      example: "Main Street",
    },
    {
      id: "housenumber",
      label: "House Number",
      description: "House/building number",
      type: "string",
      apiResponsePath: "features[0].properties.housenumber",
      example: "123",
    },
    {
      id: "locality",
      label: "City/Locality",
      description: "City or locality name",
      type: "string",
      apiResponsePath: "features[0].properties.locality",
      example: "New York",
    },
    {
      id: "postalcode",
      label: "Postal Code",
      description: "ZIP/postal code",
      type: "string",
      apiResponsePath: "features[0].properties.postalcode",
      example: "10001",
    },
    {
      id: "county",
      label: "County",
      description: "County name",
      type: "string",
      apiResponsePath: "features[0].properties.county",
      example: "New York County",
    },
    {
      id: "region",
      label: "Region",
      description: "Region/state/province name",
      type: "string",
      apiResponsePath: "features[0].properties.region",
      example: "New York",
    },
    {
      id: "region_a",
      label: "Region Code",
      description: "Region abbreviation/code",
      type: "string",
      apiResponsePath: "features[0].properties.region_a",
      example: "NY",
    },
    {
      id: "country",
      label: "Country",
      description: "Country name",
      type: "string",
      apiResponsePath: "features[0].properties.country",
      example: "United States",
    },
    {
      id: "country_code",
      label: "Country Code",
      description: "ISO country code (2-letter)",
      type: "string",
      apiResponsePath: "features[0].properties.country_code",
      example: "US",
    },
    {
      id: "country_a",
      label: "Country Code (3-letter)",
      description: "ISO country code (3-letter)",
      type: "string",
      apiResponsePath: "features[0].properties.country_a",
      example: "USA",
    },
    {
      id: "localadmin",
      label: "Local Admin",
      description: "Local administrative area",
      type: "string",
      apiResponsePath: "features[0].properties.localadmin",
      example: "Manhattan",
    },
    {
      id: "source",
      label: "Source",
      description: "Data source (e.g., openstreetmap, wof)",
      type: "string",
      apiResponsePath: "features[0].properties.source",
      example: "openstreetmap",
    },
    {
      id: "source_id",
      label: "Source ID",
      description: "ID from the source dataset",
      type: "string",
      apiResponsePath: "features[0].properties.source_id",
      example: "way/224094258",
    },
    {
      id: "gid",
      label: "GID",
      description: "Global identifier (Who's On First GID)",
      type: "string",
      apiResponsePath: "features[0].properties.gid",
      example: "openstreetmap:address:way/224094258",
    },
    {
      id: "layer",
      label: "Layer",
      description: "Feature layer type (address, venue, etc.)",
      type: "string",
      apiResponsePath: "features[0].properties.layer",
      example: "address",
    },
    {
      id: "id",
      label: "ID",
      description: "Feature ID",
      type: "string",
      apiResponsePath: "features[0].properties.id",
      example: "way/224094258",
    },
    {
      id: "bbox_min_lon",
      label: "Bounding Box Min Longitude",
      description: "Minimum longitude of bounding box",
      type: "number",
      apiResponsePath: "bbox[0]",
      example: "-74.0060",
    },
    {
      id: "bbox_min_lat",
      label: "Bounding Box Min Latitude",
      description: "Minimum latitude of bounding box",
      type: "number",
      apiResponsePath: "bbox[1]",
      example: "40.7128",
    },
    {
      id: "bbox_max_lon",
      label: "Bounding Box Max Longitude",
      description: "Maximum longitude of bounding box",
      type: "number",
      apiResponsePath: "bbox[2]",
      example: "-74.0060",
    },
    {
      id: "bbox_max_lat",
      label: "Bounding Box Max Latitude",
      description: "Maximum latitude of bounding box",
      type: "number",
      apiResponsePath: "bbox[3]",
      example: "40.7128",
    },
  ],
};

/**
 * Registry of all available enhancement services
 */
const enhancementServices: Map<string, EnhancementService> = new Map([
  [geocodingService.id, geocodingService],
]);

/**
 * Get an enhancement service by ID
 */
export function getEnhancementService(
  id: string
): EnhancementService | undefined {
  return enhancementServices.get(id);
}

/**
 * Get all available enhancement services
 */
export function getAllEnhancementServices(): EnhancementService[] {
  return Array.from(enhancementServices.values());
}
